import {Box, Popover, PopoverProps} from '@mui/material';
import {isMac} from '@wandb/weave/common/util/browser';
import {Button} from '@wandb/weave/components/Button';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {ResizableBox} from 'react-resizable';

import {DraggableGrow, DraggableHandle} from '../../../../DraggablePopups';
import {CodeEditor} from './editors/CodeEditor';
import {DiffEditor} from './editors/DiffEditor';
import {TextEditor} from './editors/TextEditor';

export type EditorMode = 'text' | 'code' | 'diff';

// Helper functions for value conversion
export const isJsonList = (value: any): boolean => Array.isArray(value);

export const stringifyValue = (value: any): string =>
  isJsonList(value)
    ? JSON.stringify(value, null, 2)
    : typeof value === 'string'
      ? value
      : String(value);

export const parseJsonList = (
  value: string
): {parsed?: any[]; error?: string} => {
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return {parsed};
    }
    return {error: 'Value must be a JSON array'};
  } catch (err) {
    return {error: (err as Error).message};
  }
};

interface EditPopoverProps {
  anchorEl: HTMLElement | null;
  onClose: (value?: string | any[], keepFocus?: boolean) => void;
  initialWidth?: number;
  initialHeight?: number;
  initialEditorMode?: EditorMode;
  value: string | any[];
  originalValue?: string | any[];
  onChange: (value: string | any[]) => void;
  inputRef?: React.RefObject<HTMLTextAreaElement>;
  shouldAutoFocus?: boolean;
  onFocusRequested?: () => void;
}

export const EditPopover: React.FC<EditPopoverProps> = ({
  anchorEl,
  onClose,
  initialWidth = 400,
  initialHeight = 300,
  initialEditorMode = 'text',
  value,
  originalValue = '',
  onChange,
  inputRef,
  shouldAutoFocus = false,
  onFocusRequested,
}) => {
  const valueIsJsonList = isJsonList(value);
  const stringifiedValue = stringifyValue(value);
  const stringifiedOriginal = stringifyValue(originalValue);

  // Using a key derived from the stringified value to force re-initialization
  // of the editor state when the external value changes significantly
  const editorStateKey = stringifiedValue;

  const [editorMode, setEditorMode] =
    React.useState<EditorMode>(initialEditorMode);
  const [boxWidth, setBoxWidth] = useState(initialWidth);
  const [boxHeight, setBoxHeight] = useState(initialHeight);
  const boxRef = useRef<HTMLDivElement>(null);
  const hasInitializedSize = useRef(false);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [currentEditorValue, setCurrentEditorValue] =
    useState<string>(stringifiedValue);
  const [hasFocused, setHasFocused] = useState(false);
  const [focusRequested, setFocusRequested] = useState(false);

  // Handle initial focus based on shouldAutoFocus prop
  useEffect(() => {
    if (shouldAutoFocus && !hasFocused) {
      setTimeout(() => {
        if (inputRef?.current) {
          inputRef.current.focus();
          inputRef.current.select();
        } else {
          const textarea = document.querySelector(
            'textarea:last-of-type'
          ) as HTMLTextAreaElement;
          if (textarea) {
            textarea.focus();
            textarea.select();
          }
        }
        setHasFocused(true);
      }, 50);
    }
  }, [shouldAutoFocus, hasFocused, inputRef]);

  // Focus the popover box when it opens (for keyboard navigation)
  useEffect(() => {
    if (anchorEl && !shouldAutoFocus) {
      setTimeout(() => {
        if (boxRef.current) {
          boxRef.current.focus();
        }
      }, 100);
    }
  }, [anchorEl, shouldAutoFocus]);

  // Handle focus when explicitly requested (via Enter key)
  useEffect(() => {
    if (focusRequested) {
      const timer = setTimeout(() => {
        const muiTextarea = document.querySelector(
          '.MuiInputBase-input'
        ) as HTMLTextAreaElement;
        if (muiTextarea) {
          muiTextarea.focus();
          const len = muiTextarea.value.length;
          muiTextarea.setSelectionRange(len, len);
        } else if (inputRef?.current) {
          inputRef.current.focus();
          const len = inputRef.current.value.length;
          inputRef.current.setSelectionRange(len, len);
        }
        setFocusRequested(false);
      }, 50);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [focusRequested, inputRef]);

  // Calculate position directly from anchorEl when rendering
  let position = null;
  if (anchorEl) {
    const rect = anchorEl.getBoundingClientRect();
    const isInTopHalf = rect.top < window.innerHeight / 2;

    position = {
      left: rect.left + window.scrollX,
      top: isInTopHalf
        ? rect.bottom + window.scrollY - 36 // Move up by 36px when below
        : rect.top + window.scrollY - initialHeight + 36, // Move down by 36px when above
    };
  }

  // Only calculate the initial size once when the component mounts
  useEffect(() => {
    if (boxRef.current && !hasInitializedSize.current) {
      const contentWidth = Math.max(boxRef.current.scrollWidth, initialWidth);
      const contentHeight = Math.max(
        boxRef.current.scrollHeight,
        initialHeight
      );

      // Limit sizes to avoid excessive popover
      const maxWidth = Math.min(window.innerWidth * 0.8, 1000);
      const maxHeight = Math.min(window.innerHeight * 0.8, 800);

      setBoxWidth(Math.min(contentWidth, maxWidth));
      setBoxHeight(Math.min(contentHeight, maxHeight));

      hasInitializedSize.current = true;
    }
  }, [initialWidth, initialHeight]);

  // Handle resize events from ResizableBox
  const handleResize = (
    _e: any,
    data: {size: {width: number; height: number}}
  ) => {
    setBoxWidth(data.size.width);
    setBoxHeight(data.size.height);
  };

  // Process value changes and validate JSON when needed
  const handleValueChange = (newValue: string) => {
    setCurrentEditorValue(newValue);

    if (valueIsJsonList) {
      const {parsed, error} = parseJsonList(newValue);
      setJsonError(error || null);
      onChange(error ? newValue : parsed!);
    } else {
      onChange(newValue);
    }
  };

  // Handle closing with proper value conversion
  const handleClose = (finalValue?: string, keepFocus?: boolean) => {
    // Use currentEditorValue as the default value if finalValue is not provided
    const valueToUse =
      finalValue !== undefined ? finalValue : currentEditorValue;

    if (valueToUse !== undefined) {
      if (valueIsJsonList) {
        const {parsed, error} = parseJsonList(valueToUse);
        if (error) {
          setJsonError(error);
          return; // Don't close if we have an error
        }
        onClose(parsed, keepFocus);
      } else {
        onClose(valueToUse, keepFocus);
      }
    } else {
      onClose(undefined, keepFocus);
    }
  };

  // Handle focusing the textarea when Enter is pressed
  const handleFocusTextarea = useCallback(() => {
    onFocusRequested?.();
    setFocusRequested(true);
  }, [onFocusRequested]);

  const renderEditor = () => {
    switch (editorMode) {
      case 'text':
        return (
          <TextEditor
            key={`text-${editorStateKey}-${focusRequested}`}
            value={currentEditorValue}
            onChange={handleValueChange}
            onClose={value => handleClose(value, true)}
            inputRef={inputRef}
            autoFocus={shouldAutoFocus || focusRequested}
          />
        );
      case 'code':
        return (
          <CodeEditor
            key={`code-${editorStateKey}`}
            value={currentEditorValue}
            onChange={handleValueChange}
            onClose={value => handleClose(value, true)}
            language={valueIsJsonList ? 'json' : undefined}
            disableClosing={valueIsJsonList && jsonError !== null}
          />
        );
      case 'diff':
        return (
          <DiffEditor
            key={`diff-${editorStateKey}`}
            value={currentEditorValue}
            originalValue={stringifiedOriginal}
            onChange={handleValueChange}
            onClose={value => handleClose(value, true)}
            language={valueIsJsonList ? 'json' : undefined}
            disableClosing={valueIsJsonList && jsonError !== null}
          />
        );
      default:
        return null;
    }
  };

  // Handle MUI Popover close events separately from our value-based close
  const handlePopoverClose: PopoverProps['onClose'] = () => {
    // Don't close the popover if JSON is invalid
    if (valueIsJsonList && jsonError !== null) {
      return;
    }

    // When closing via backdrop or escape key, keep focus on the cell
    onClose(undefined, true);
  };

  if (!position) {
    return null;
  }

  return (
    <Popover
      open={!!anchorEl}
      anchorReference="anchorPosition"
      anchorPosition={position}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      onClose={handlePopoverClose}
      TransitionComponent={DraggableGrow}
      sx={{
        '& .MuiPaper-root': {
          backgroundColor: 'white',
          boxShadow:
            '0px 4px 20px rgba(0, 0, 0, 0.15), 0px 0px 40px rgba(0, 0, 0, 0.05)',
          border: '1px solid rgba(0, 0, 0, 0.2)',
          borderRadius: '4px',
          overflow: 'hidden',
          padding: '0px',
        },
      }}>
      <ResizableBox
        width={boxWidth}
        height={boxHeight}
        minConstraints={[initialWidth, initialHeight]}
        resizeHandles={['se']}
        onResize={handleResize}
        handle={
          <div
            className="react-resizable-handle react-resizable-handle-se"
            style={{
              position: 'absolute',
              right: 0,
              bottom: 0,
              width: '20px',
              height: '20px',
              cursor: 'se-resize',
              zIndex: 1000,
            }}
          />
        }>
        <Box
          ref={boxRef}
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter' && editorMode === 'text') {
              e.preventDefault();
              e.stopPropagation();
              handleFocusTextarea();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              handleClose(undefined, true);
            }
          }}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            outline: 'none', // Remove focus outline
            '&:focus': {
              outline: 'none', // Remove focus outline on focus
            },
            '& .monaco-editor, & .monaco-diff-editor': {
              position: 'relative',
              zIndex: 1,
            },
          }}>
          <DraggableHandle>
            <Box
              sx={{
                cursor: 'grab',
                backgroundColor: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
                borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
              }}>
              <Box>
                {!valueIsJsonList && (
                  <Button
                    tooltip="Text mode"
                    icon="text-language"
                    size="small"
                    variant={editorMode === 'text' ? 'secondary' : 'ghost'}
                    onClick={() => setEditorMode('text')}
                  />
                )}
                <Button
                  tooltip="Code mode"
                  icon="code-alt"
                  size="small"
                  variant={editorMode === 'code' ? 'secondary' : 'ghost'}
                  onClick={() => setEditorMode('code')}
                />
                <Button
                  tooltip="Diff mode"
                  icon="diff"
                  size="small"
                  variant={editorMode === 'diff' ? 'secondary' : 'ghost'}
                  onClick={() => setEditorMode('diff')}
                />
              </Box>
              <Box sx={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                <p
                  style={{
                    color: 'var(--moon-500)',
                    margin: '0px',
                    fontSize: '12px',
                    fontWeight: '500',
                  }}>
                  {shouldAutoFocus || hasFocused
                    ? `${isMac ? 'CMD' : 'CTRL'}+Enter to save`
                    : 'Press Enter to edit'}
                </p>
                <Button
                  tooltip="Close"
                  icon="close"
                  size="small"
                  variant="ghost"
                  disabled={valueIsJsonList && jsonError !== null}
                  onClick={e => {
                    e.preventDefault();
                    handleClose(currentEditorValue, true);
                  }}
                />
              </Box>
            </Box>
          </DraggableHandle>
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              padding: editorMode === 'text' ? '12px' : '0px',
            }}>
            {renderEditor()}
          </Box>
          {jsonError && (
            <Box
              sx={{
                padding: '8px 12px',
                backgroundColor: '#FFEBE6',
                color: '#BF2600',
                borderTop: '1px solid #FF8F73',
                fontSize: '12px',
                fontFamily: 'monospace',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
              {jsonError}
            </Box>
          )}
        </Box>
      </ResizableBox>
    </Popover>
  );
};
